import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Form,
    Input,
    Select,
    Switch,
    Button,
    Space,
    Divider,
    Typography,
    InputNumber,
    message,
} from 'antd';
import { FormField_V2, FieldType_V2, SelectOption, SqliteFieldConfig, FormSection } from '../../../common/typescript/models/CollectFormV2';
import { SqliteQueryConfigModal } from './SqliteQueryConfigModal';
import { PiCheckBold } from 'react-icons/pi';

const { TextArea } = Input;
const { Title, Text } = Typography;

type FieldConfigDrawerProps = {
    visible: boolean;
    field: FormField_V2 | null;
    mode: 'create' | 'edit';
    onClose: () => void;
    onSave: (field: FormField_V2) => void;
    existingFieldIds: string[];
    uploadedDatabases: string[]; // File names
    sqliteFiles: File[]; // Actual File objects
    sections: FormSection[];
};

export const FieldConfigDrawer: React.FC<FieldConfigDrawerProps> = ({
    visible,
    field,
    mode,
    onClose,
    onSave,
    existingFieldIds,
    uploadedDatabases,
    sqliteFiles,
    sections,
}) => {
    const [form] = Form.useForm();
    const [fieldType, setFieldType] = useState<FieldType_V2>(FieldType_V2.TEXT);
    const [staticOptions, setStaticOptions] = useState<string>('');
    const [sqliteConfig, setSqliteConfig] = useState<SqliteFieldConfig | undefined>(undefined);
    const [sqliteModalVisible, setSqliteModalVisible] = useState(false);

    useEffect(() => {
        if (field) {
            form.setFieldsValue({
                id: field.id,
                label: field.label,
                type: field.type,
                required: field.required || false,
                placeholder: field.placeholder || '',
                helpText: field.helpText || '',
                defaultValue: field.defaultValue || '',
                validation: field.validation || {},
                config: field.config || {},
                section: field.section,
            });
            setFieldType(field.type);

            // Load static options if SELECT field
            if (field.type === FieldType_V2.SELECT && field.config) {
                const selectConfig = field.config as any;
                if (selectConfig.options) {
                    const optionsText = selectConfig.options
                        .map((opt: SelectOption) => `${opt.value}:${opt.label}`)
                        .join('\n');
                    setStaticOptions(optionsText);
                }
                if (selectConfig.sqlite) {
                    setSqliteConfig(selectConfig.sqlite);
                }
            }
        } else {
            form.resetFields();
            setStaticOptions('');
            setSqliteConfig(undefined);
        }
    }, [field, form]);

    const handleSave = () => {
        form
            .validateFields()
            .then((values) => {
                // Validate unique ID
                if (mode === 'create' && existingFieldIds.includes(values.id)) {
                    message.error(`ID trường "${values.id}" đã tồn tại`);
                    return;
                }

                // Build field config based on type
                let config: any = {};

                if ([FieldType_V2.SELECT, FieldType_V2.RADIO, FieldType_V2.CHECKBOX].includes(values.type)) {
                    // Parse static options
                    if (staticOptions.trim()) {
                        const options: SelectOption[] = staticOptions
                            .split('\n')
                            .filter((line) => line.trim())
                            .map((line) => {
                                const [value, label] = line.split(':').map((s) => s.trim());
                                return { value, label: label || value };
                            });
                        config = { ...config, options };
                    }
                    // Add SQLite config if exists
                    if (sqliteConfig) {
                        config = { ...config, sqlite: sqliteConfig };
                    }
                }

                // Merge other configs
                if (values.config) {
                    config = { ...config, ...values.config };
                }

                const newField: FormField_V2 = {
                    id: values.id,
                    label: values.label,
                    type: values.type,
                    section: values.section,
                    required: values.required,
                    placeholder: values.placeholder,
                    helpText: values.helpText,
                    defaultValue: values.defaultValue,
                    config: Object.keys(config).length > 0 ? config : undefined,
                    validation: values.validation,
                };

                onSave(newField);
                form.resetFields();
                setStaticOptions('');
                setSqliteConfig(undefined);
            })
            .catch((err) => {
                console.error('Validation failed:', err);
            });
    };

    return (
        <Drawer
            title={mode === 'create' ? 'Thêm trường mới' : 'Chỉnh sửa trường'}
            width={480}
            open={visible}
            onClose={onClose}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onClose}>Hủy</Button>
                    <Button type="primary" onClick={handleSave}>
                        {mode === 'create' ? 'Thêm trường' : 'Lưu thay đổi'}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="id"
                    label="ID trường"
                    rules={[
                        { required: true, message: 'Vui lòng nhập ID trường' },
                        {
                            pattern: /^[a-z_][a-z0-9_]*$/,
                            message: 'ID phải viết thường, bắt đầu bằng chữ cái, chỉ dùng chữ, số và gạch dưới',
                        },
                    ]}
                    extra="Định danh duy nhất (vd: ho_ten, ngay_sinh)"
                >
                    <Input placeholder="vd: ho_ten" disabled={mode === 'edit'} />
                </Form.Item>

                <Form.Item
                    name="label"
                    label="Nhãn hiển thị"
                    rules={[{ required: true, message: 'Vui lòng nhập nhãn hiển thị' }]}
                >
                    <Input placeholder="vd: Họ và tên" />
                </Form.Item>

                {sections.length > 0 && (
                    <Form.Item name="section" label="Thuộc phần (Section)">
                        <Select allowClear placeholder="Chọn phần">
                            {sections.map((s) => (
                                <Select.Option key={s.id} value={s.id}>
                                    {s.title}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item name="type" label="Loại trường" rules={[{ required: true }]}>
                    <Select
                        placeholder="Chọn loại trường"
                        onChange={(value) => setFieldType(value)}
                        disabled={mode === 'edit'}
                    >
                        <Select.Option value={FieldType_V2.TEXT}>Văn bản</Select.Option>
                        <Select.Option value={FieldType_V2.TEXTAREA}>Văn bản dài</Select.Option>
                        <Select.Option value={FieldType_V2.NUMBER}>Số</Select.Option>
                        <Select.Option value={FieldType_V2.DATE}>Ngày</Select.Option>
                        <Select.Option value={FieldType_V2.DATETIME}>Ngày giờ</Select.Option>
                        <Select.Option value={FieldType_V2.SELECT}>Chọn</Select.Option>
                        <Select.Option value={FieldType_V2.CHECKBOX}>Hộp kiểm</Select.Option>
                        <Select.Option value={FieldType_V2.RADIO}>Một lựa chọn (Radio)</Select.Option>
                        <Select.Option value={FieldType_V2.FILE}>Tệp / Ảnh</Select.Option>
                        <Select.Option value={FieldType_V2.HEADER}>Tiêu đề</Select.Option>
                        <Select.Option value={FieldType_V2.DIVIDER}>Đường kẻ</Select.Option>
                        <Select.Option value={FieldType_V2.COMPUTED}>Tính toán</Select.Option>
                    </Select>
                </Form.Item>

                <Divider />

                <Form.Item name="required" label="Bắt buộc" valuePropName="checked">
                    <Switch />
                </Form.Item>

                <Form.Item name="placeholder" label="Gợi ý nhập liệu">
                    <Input placeholder="vd: Nhập họ và tên của bạn" />
                </Form.Item>

                <Form.Item name="helpText" label="Văn bản trợ giúp">
                    <Input placeholder="Hướng dẫn bổ sung cho người dùng" />
                </Form.Item>

                <Form.Item name="defaultValue" label="Giá trị mặc định">
                    <Input placeholder="Giá trị mặc định (tùy chọn)" />
                </Form.Item>

                {fieldType === FieldType_V2.NUMBER && (
                    <>
                        <Divider>Cấu hình số & Xác thực</Divider>
                        <Space>
                            <Form.Item name={['validation', 'min']} label="Giá trị nhỏ nhất (Min)">
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['validation', 'max']} label="Giá trị lớn nhất (Max)">
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                        </Space>
                        <Space>
                            <Form.Item name={['config', 'step']} label="Bước nhảy (Step)">
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name={['config', 'suffix']} label="Đơn vị (Suffix)">
                                <Input placeholder="vd: m², kg" />
                            </Form.Item>
                        </Space>
                    </>
                )}

                {fieldType === FieldType_V2.COMPUTED && (
                    <>
                        <Divider>Cấu hình tính toán</Divider>
                        <Form.Item
                            name={['config', 'expression']}
                            label="Công thức"
                            extra="Ví dụ: land_area * unit_price"
                            rules={[{ required: true, message: 'Vui lòng nhập công thức' }]}
                        >
                            <Input placeholder="vd: field_a * field_b" />
                        </Form.Item>
                        <Form.Item name={['config', 'format']} label="Định dạng">
                            <Input placeholder="vd: 0,0.00" />
                        </Form.Item>
                    </>
                )}

                {[FieldType_V2.SELECT, FieldType_V2.RADIO, FieldType_V2.CHECKBOX].includes(fieldType) && (
                    <>
                        <Divider>Tùy chọn danh sách</Divider>
                        <Form.Item
                            label="Tùy chọn tĩnh"
                            extra="Nhập mỗi tùy chọn trên một dòng theo định dạng: giá_trị:nhãn_hiển_thị (vd: 1:Nam)"
                        >
                            <TextArea
                                rows={6}
                                value={staticOptions}
                                onChange={(e) => setStaticOptions(e.target.value)}
                                placeholder="1:Nam&#10;2:Nữ&#10;3:Khác"
                            />
                        </Form.Item>
                        <Button
                            type="dashed"
                            block
                            onClick={() => setSqliteModalVisible(true)}
                            style={{ marginTop: 8 }}
                        >
                            {sqliteConfig ? 'Chỉnh sửa truy vấn SQLite' : 'Cấu hình truy vấn SQLite'}
                        </Button>
                        {sqliteConfig && (
                            <Text type="success" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: 8 }}>
                                <PiCheckBold size={13} /> Đã cấu hình: {sqliteConfig.table}.{sqliteConfig.valueColumn}
                            </Text>
                        )}
                    </>
                )}
            </Form>

            {/* SQLite Query Config Modal */}
            <SqliteQueryConfigModal
                visible={sqliteModalVisible}
                uploadedDatabases={uploadedDatabases}
                currentConfig={sqliteConfig}
                onClose={() => setSqliteModalVisible(false)}
                onSave={(config) => {
                    setSqliteConfig(config);
                    setSqliteModalVisible(false);
                    message.success('Đã cấu hình truy vấn SQLite');
                }}
                sqliteFiles={sqliteFiles}
            />
        </Drawer>
    );
};
